/**
 * 저장 공간 경고 — 배정·바로 보내기를 고르는 순간 "이 화면에는 안 들어간다" 를 말한다.
 *
 * 공간이 바닥난 화면에 큰 영상을 배정해도 아무 오류가 나지 않는다. 화면은 받다가 멈추고 웹뷰는 받아 둔
 * 파일을 임의로 버려, 재생이 조용히 구멍 난다 — 사람은 며칠 뒤 화면 앞에서야 안다. 셈은 서버가 한다
 * (`POST /displays/storage-check`: 재생본 크기, 화면이 이미 들고 있는 파일, 꺼 둔 줄).
 *
 * **막지 않는다.** 화면이 알려 온 여유는 근사값이고(`quota` 는 브라우저 할당량), 지금 들고 있는 것을
 * 지우면 들어갈 수도 있다. **모르면 말하지 않는다** — 서버가 `fits: null` 로 준 화면은 그리지 않는다.
 */
import type { StorageCheckInput, StorageCheckResult } from '../api/client.js';
import { formatBytes } from './ui.js';

export interface StorageShortfall {
  id: string;
  name: string;
  /** 모자라는 양 — 더 받을 양에서 여유를 뺀 것. */
  shortfall: number;
}

/** 안 들어가는 화면만, 많이 모자라는 것부터. 모르는 화면(`fits: null`)은 넣지 않는다. */
export function storageShortfalls(result: StorageCheckResult | null | undefined): StorageShortfall[] {
  return (result?.items ?? [])
    .filter((row) => row.fits === false && row.free_bytes !== null)
    .map((row) => ({ id: row.display_id, name: row.name, shortfall: row.required_bytes - (row.free_bytes ?? 0) }))
    .sort((a, b) => b.shortfall - a.shortfall || a.name.localeCompare(b.name, 'ko'));
}

/**
 * 경고 한 줄 — "로비(1.2 GB 부족) · 2층(300.0 MB 부족) 외 3대".
 *
 * 화면 이름과 모자라는 양을 함께 적는다. "공간이 모자란 화면이 있습니다" 만으로는 어느 화면을 빼고
 * 배정해야 할지, 무엇을 줄여야 할지 알 수 없다. 많으면 앞의 몇 대만 적고 나머지는 수로 말한다.
 */
export function storageWarningText(shortfalls: readonly StorageShortfall[], limit = 3): string | null {
  if (shortfalls.length === 0) return null;
  const named = shortfalls
    .slice(0, limit)
    .map((entry) => `${entry.name || '(이름 없음)'}(${formatBytes(entry.shortfall)} 부족)`)
    .join(' · ');
  const rest = shortfalls.length - limit;
  return rest > 0 ? `${named} 외 ${rest}대` : named;
}

/**
 * 같은 물음이면 같은 열쇠 — 화면 순서가 달라도 같다. 그새 다시 물은 답을 버릴지 가를 때 쓴다.
 * 물을 것이 없으면(편성을 고르지 않았거나 화면이 없으면) 빈 문자열.
 */
export function storageCheckKey(input: StorageCheckInput | null): string {
  if (!input || input.display_ids.length === 0) return '';
  const ids = [...new Set(input.display_ids)].sort();
  if ('playlist_id' in input) return `p:${input.playlist_id}|${ids.join(',')}`;
  if ('schedule_id' in input) return `s:${input.schedule_id}|${ids.join(',')}`;
  if (input.asset_ids.length === 0) return '';
  return `a:${input.asset_ids.join(',')}|${ids.join(',')}`;
}

/** 배정 값(`playlist:<id>` · `schedule:<id>`)을 물음으로. 비었거나 모르는 값이면 null. */
export function assignmentCheckInput(displayIds: readonly string[], value: string): StorageCheckInput | null {
  if (displayIds.length === 0) return null;
  const [kind, id] = value.split(':');
  if (!id) return null;
  if (kind === 'playlist') return { display_ids: [...displayIds], playlist_id: id };
  if (kind === 'schedule') return { display_ids: [...displayIds], schedule_id: id };
  return null;
}
